import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Sparkles, ArrowRight } from 'lucide-react';

/**
 * Featured "Song of the Week" banner shown on the Dashboard. Uses the
 * public `GET /api/song-of-week` endpoint, which returns either the admin's
 * manual pick for the current week or an auto-picked song based on the
 * last 7 days of learners activity.
 */
export default function SongOfWeekHero() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/song-of-week');
        if (alive) setData(res.data);
      } catch (e) { /* silent fallback */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !data?.song) return null;
  const s = data.song;

  return (
    <section
      className="relative mb-10 rounded-3xl overflow-hidden border border-orange-800/40 bg-gradient-to-br from-stone-900 via-stone-900 to-orange-950/40 rise"
      data-testid="song-of-week-hero"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto]">
        <div className="p-6 md:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-700/20 border border-orange-800/60 text-orange-300 text-[10px] font-semibold uppercase tracking-wider">
              <Sparkles size={11} /> Chanson de la semaine
            </div>
            <span className="text-[10px] text-stone-500 uppercase tracking-wider">
              {data.is_manual ? "Sélection éditoriale" : "Tendance de la semaine"}
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight text-stone-100">
            {s.title}
          </h2>
          <div className="text-stone-400 text-sm mt-1">
            {s.artist}{s.genre ? ` · ${s.genre}` : ''}{s.difficulty ? ` · ${s.difficulty}` : ''}
          </div>
          {s.description && (
            <p className="mt-3 text-stone-400 text-sm max-w-xl line-clamp-2">
              {s.description}
            </p>
          )}
          <Link
            to={`/song/${s.id}`}
            data-testid="sotw-cta"
            className="mt-4 inline-flex items-center gap-1.5 self-start px-4 py-2 rounded-full bg-orange-700 hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
          >
            Ouvrir la chanson <ArrowRight size={14} />
          </Link>
        </div>
        <div className="relative h-40 md:h-auto md:w-64 lg:w-80 shrink-0 overflow-hidden">
          {s.cover_url ? (
            <img
              src={s.cover_url}
              alt={s.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-stone-800 flex items-center justify-center text-stone-600">
              ♪
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-stone-900" />
        </div>
      </div>
    </section>
  );
}
